/* eslint-disable import/no-extraneous-dependencies */
import { useEffect, useRef } from 'react';
import type videojs from 'video.js';
import 'video.js/dist/video-js.css';
import '../../../styles/videojs-city-theme.css';

type VideoJsPlayerInstance = ReturnType<typeof videojs>;

interface VideoJsPlayerProps {
  options: IVideoOptionsProps;
  onReady?: (player: VideoJsPlayerInstance) => void;
}

export function VideoJsPlayer({ options, onReady }: VideoJsPlayerProps) {
  const videoRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<VideoJsPlayerInstance | null>(null);
  const videojsRef = useRef<typeof videojs | null>(null);

  useEffect(() => {
    let cancelled = false;

    const initPlayer = async () => {
      if (!videojsRef.current) {
        const mod = await import('video.js');
        if (cancelled) return;
        videojsRef.current = mod.default;
      }

      const vjs = videojsRef.current;

      if (!playerRef.current && videoRef.current) {
        const videoElement = document.createElement('video-js');
        videoElement.classList.add('vjs-big-play-centered');
        videoRef.current.appendChild(videoElement);

        // eslint-disable-next-line no-multi-assign
        const player = (playerRef.current = vjs(videoElement, options, () => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          onReady && onReady(player);
        }));
      } else if (playerRef.current) {
        playerRef.current.autoplay(options.autoplay);
        playerRef.current.src(options.sources);
      }
    };

    initPlayer();

    return () => {
      cancelled = true;
    };
  }, [options, onReady]);

  // Dispose the Video.js player when component unmounts
  useEffect(() => {
    const player = playerRef.current;
    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  return (
    <div
      data-vjs-player
      style={{
        objectFit: 'cover',
        aspectRatio: '16 / 9',
        width: '100%',
      }}
      className="w-full h-auto video-js vjs-theme-city vjs-picture-in-picture-control"
    >
      <div ref={videoRef} />
    </div>
  );
}

export default VideoJsPlayer;
